import {useState, useEffect, useMemo} from 'react'
import {
    Box,
    Typography,
    CircularProgress,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {fetchS3File} from './shared'

interface ComparisonField {
    field: string
    original: any
    generated: any
}

interface DataComparisonTabProps {
    record: any
}

function formatValue(value: any): string {
    if (value === null) return 'null'
    if (value === undefined) return 'undefined'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

function getDiffStyle(original: any, generated: any): React.CSSProperties {
    const origStr = formatValue(original)
    const genStr = formatValue(generated)
    if (origStr === genStr) return {}
    return {backgroundColor: '#fff3e0'}
}

function DataComparisonTab({record}: DataComparisonTabProps) {
    const [data, setData] = useState<Record<string, Record<string, ComparisonField[]>> | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null)

    const s3Ref = record?.data?.responseS3?.dataComparison

    useEffect(() => {
        if (!s3Ref?.bucketName || !s3Ref?.key) return
        setLoading(true)
        setError(null)
        fetchS3File(s3Ref)
            .then(result => {
                setData(result)
                const domains = Object.keys(result || {})
                if (domains.length > 0) setSelectedDomain(domains[0])
            })
            .catch((err: any) => setError(err.message))
            .finally(() => setLoading(false))
    }, [s3Ref?.bucketName, s3Ref?.key])

    const domainStats = useMemo(() => {
        if (!data) return {}
        const stats: Record<string, {records: number; fields: number}> = {}
        for (const domain of Object.keys(data)) {
            const keys = data[domain]
            let fields = 0
            for (const pk of Object.keys(keys)) {
                fields += keys[pk].length
            }
            stats[domain] = {records: Object.keys(keys).length, fields}
        }
        return stats
    }, [data])

    const filteredRecords = useMemo(() => {
        if (!data || !selectedDomain || !data[selectedDomain]) return null
        const records = data[selectedDomain]
        if (!search.trim()) return records

        const q = search.toLowerCase()
        const result: Record<string, ComparisonField[]> = {}
        for (const pk of Object.keys(records)) {
            if (pk.toLowerCase().includes(q)) {
                result[pk] = records[pk]
            } else {
                const matchingFields = records[pk].filter(f => f.field.toLowerCase().includes(q))
                if (matchingFields.length > 0) {
                    result[pk] = matchingFields
                }
            }
        }
        return result
    }, [data, selectedDomain, search])

    if (loading) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, p: 2}}>
                <CircularProgress size={20}/>
                <span>Loading data comparison...</span>
            </Box>
        )
    }

    if (error) {
        return <Box sx={{color: 'red', p: 2}}>Error: {error}</Box>
    }

    if (!data || Object.keys(data).length === 0) {
        return <Box sx={{color: '#888', fontStyle: 'italic', p: 2}}>No data comparison available</Box>
    }

    const domains = Object.keys(data)

    return (
        <Box sx={{display: 'flex', height: 'calc(90vh - 260px)'}}>
            {/* Sidebar */}
            <Box sx={{
                width: 240,
                minWidth: 240,
                borderRight: '1px solid #e0e0e0',
                overflow: 'auto',
                backgroundColor: '#fafafa'
            }}>
                <List disablePadding dense>
                    {domains.map(domain => (
                        <ListItemButton
                            key={domain}
                            selected={selectedDomain === domain}
                            onClick={() => {
                                setSelectedDomain(domain)
                                setSearch('')
                            }}
                            sx={{
                                py: 1,
                                borderBottom: '1px solid #f0f0f0',
                                '&.Mui-selected': {
                                    backgroundColor: '#e3f2fd',
                                    '&:hover': {backgroundColor: '#bbdefb'}
                                }
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8rem'}}>
                                        {domain}
                                    </Typography>
                                }

                            />
                        </ListItemButton>
                    ))}
                </List>
            </Box>

            {/* Content */}
            <Box sx={{flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column'}}>
                {selectedDomain && (
                    <>
                        <Box sx={{
                            p: 1.5,
                            borderBottom: '1px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: '#fff',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                        }}>
                            <Typography variant="subtitle1" sx={{fontWeight: 700}}>{selectedDomain}</Typography>
                            <TextField
                                size="small"
                                placeholder="Search key or field..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{width: 250}}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small"/>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Chip
                                label={`${Object.keys(filteredRecords || {}).length} records`}
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{flex: 1, overflow: 'auto'}}>
                            {filteredRecords && Object.keys(filteredRecords).map(primaryKey => (
                                <Box key={primaryKey}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            backgroundColor: '#f5f5f5',
                                            borderBottom: '1px solid #e0e0e0',
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 0
                                        }}
                                    >
                                        #{primaryKey}
                                        <Chip
                                            label={`${filteredRecords[primaryKey].length} fields`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ml: 1, height: 18, fontSize: '0.7rem'}}
                                        />
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{fontWeight: 600, width: '33%'}}>Field</TableCell>
                                                    <TableCell sx={{fontWeight: 600, width: '33%'}}>Original</TableCell>
                                                    <TableCell sx={{fontWeight: 600, width: '33%'}}>Generated</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredRecords[primaryKey].map((row, idx) => (
                                                    <TableRow key={idx} sx={getDiffStyle(row.original, row.generated)}>
                                                        <TableCell sx={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                                                            {row.field}
                                                        </TableCell>
                                                        <TableCell sx={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                                                            {formatValue(row.original)}
                                                        </TableCell>
                                                        <TableCell sx={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                                                            {formatValue(row.generated)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                            {filteredRecords && Object.keys(filteredRecords).length === 0 && (
                                <Box sx={{p: 2, color: '#888', fontStyle: 'italic'}}>No matching records</Box>
                            )}
                        </Box>
                    </>
                )}
                {!selectedDomain && (
                    <Box sx={{p: 2, color: '#888', fontStyle: 'italic'}}>Select a domain from the sidebar</Box>
                )}
            </Box>
        </Box>
    )
}

export default DataComparisonTab
